import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from './baseQueryWithReauth';

export interface Contact { id: string; name: string; }
export interface Deal {
  id: string;
  title: string;
  status?: string;
  ticketId?: string;
  value?: number;
}

export const crmApi = createApi({
  reducerPath: 'crmApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Contact', 'Deal'],
  endpoints: (builder) => ({
    getContacts: builder.query<Contact[], void>({
      query: () => ({ url: '/crm/contacts' }),
      providesTags: ['Contact'],
    }),
    createContact: builder.mutation<Contact, Partial<Contact>>({
      query: (body) => ({ url: '/crm/contacts', method: 'POST', body }),
      invalidatesTags: ['Contact'],
    }),
    updateContact: builder.mutation<Contact, { id: string; data: Partial<Contact> }>({
      query: ({ id, data }) => ({ url: `/crm/contacts/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['Contact'],
    }),
    deleteContact: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/crm/contacts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Contact'],
    }),
    getDeals: builder.query<Deal[], void>({
      query: () => ({ url: '/crm/deals' }),
      providesTags: ['Deal'],
    }),
    createDeal: builder.mutation<Deal, Partial<Deal>>({
      query: (body) => ({ url: '/crm/deals', method: 'POST', body }),
      invalidatesTags: ['Deal'],
    }),
    updateDeal: builder.mutation<Deal, { id: string; data: Partial<Deal> }>({
      query: ({ id, data }) => ({ url: `/crm/deals/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['Deal'],
    }),
    deleteDeal: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/crm/deals/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Deal'],
    }),
    createDealFromTicket: builder.mutation<Deal, { ticketId: string; data?: Partial<Deal> }>({
      query: ({ ticketId, data }) => ({ url: '/crm/deals/from-ticket', method: 'POST', body: { ticketId, ...data } }),
      invalidatesTags: ['Deal'],
    }),
    syncSalesforce: builder.mutation<{ status: string }, void>({
      query: () => ({ url: '/crm/sync-salesforce', method: 'POST' }),
    }),
  }),
});

export const {
  useGetContactsQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useGetDealsQuery,
  useCreateDealMutation,
  useUpdateDealMutation,
  useDeleteDealMutation,
  useCreateDealFromTicketMutation,
  useSyncSalesforceMutation,
} = crmApi;
